// ERP writes carry the authenticated UID; Firestore rules validate it at commit time.
// Keep the native SDK semantics, including transaction retries and atomic batches.
import * as firestore from 'firebase/firestore';
import { auth } from '../firebase';
export * from 'firebase/firestore';
const collections = new Set(["checklist_templates", "daily_operations", "ops_tasks", "task_configs", "attendance", "attendance_templates", "faculty_timings", "faculty_timing_defaults", "schedules", "master_schedule", "classrooms", "student_registry", "exam_results", "feedback_forms", "feedback_responses", "ops_tickets", "erp_ticket_escalations", "ir_requests", "result_sheets", "result_teams", "result_service_requests", "sales_leads", "sales_teams", "sales_sheets", "sales_call_statuses", "sales_remarks_options", "sales_ad_forms", "marketing_trips", "invoices", "finance_settings", "finance_departments", "finance_payees", "finance_banks", "finance_payment_ledger", "hr_invites", "hr_employees", "hr_leaves", "hr_attendance", "hr_attendance_requests", "hr_settings", "erp_payslips", "prp_budgets", "prp_tasks", "user_onboarding_cycles", "zoom_accounts", "app_settings", "erp_deletion_requests", "users", "sheets", "settings"]);
export function isAuditedReference(ref) {
  const parts = String(ref?.path || '').split('/');
  return (parts.length === 2 && collections.has(parts[0])) || (parts.length === 4 && parts[0] === 'sales_ad_forms' && parts[2] === 'responses');
}
function stamp(ref) {
  const uid = auth?.currentUser?.uid;
  return uid && isAuditedReference(ref) ? { uid, at: firestore.serverTimestamp(), version: 1 } : null;
}
function setArgs(ref, data, options) {
  const mark = stamp(ref);
  if (!mark) return options === undefined ? [ref, data] : [ref, data, options];
  const opts = options?.mergeFields ? { ...options, mergeFields: [...options.mergeFields, '_erpAudit'] } : options;
  return opts === undefined ? [ref, { ...data, _erpAudit: mark }] : [ref, { ...data, _erpAudit: mark }, opts];
}
function updateArgs(ref, args) {
  const mark = stamp(ref);
  if (!mark) return [ref, ...args];
  return args.length === 1 ? [ref, { ...args[0], _erpAudit: mark }] : [ref, ...args, '_erpAudit', mark];
}
export const setDoc = (ref, data, options) => firestore.setDoc(...setArgs(ref, data, options));
export const updateDoc = (ref, ...args) => firestore.updateDoc(...updateArgs(ref, args));
export async function addDoc(collection, data) {
  if (!auth?.currentUser || !isAuditedReference({ path: `${collection.path}/new` })) return firestore.addDoc(collection, data);
  const ref = firestore.doc(collection);
  await setDoc(ref, data);
  return ref;
}
function deletionReceipt(writer, ref) {
  const uid = auth?.currentUser?.uid;
  if (!uid || !isAuditedReference(ref)) return;
  // In the same atomic commit as the deletion. Rules require the record to exist
  // before the commit and be absent afterwards, and bind UID to request.auth.
  writer.set(firestore.doc(firestore.collection(ref.firestore, 'erp_audit_deletions')), {
    uid, path: ref.path, at: firestore.serverTimestamp(), version: 1,
  });
}
function wrap(writer) {
  const wrapped = {
    set(ref, data, options) { writer.set(...setArgs(ref, data, options)); return wrapped; },
    update(ref, ...args) { writer.update(...updateArgs(ref, args)); return wrapped; },
    delete(ref) { deletionReceipt(writer, ref); writer.delete(ref); return wrapped; },
  };
  if (writer.get) wrapped.get = (...args) => writer.get(...args);
  if (writer.commit) wrapped.commit = () => writer.commit();
  return wrapped;
}
export const writeBatch = db => auth?.currentUser ? wrap(firestore.writeBatch(db)) : firestore.writeBatch(db);
export const runTransaction = (db, callback, options) => firestore.runTransaction(db,
  transaction => callback(auth?.currentUser ? wrap(transaction) : transaction), options);
export async function deleteDoc(ref) {
  if (!auth?.currentUser || !isAuditedReference(ref)) return firestore.deleteDoc(ref);
  const batch = writeBatch(ref.firestore);
  batch.delete(ref);
  return batch.commit();
}
